/* eslint-disable no-nested-ternary */
import {
  DatePicker,
  InputNumber,
  Input,
  Radio,
  Select,
  Switch,
  Tooltip,
  Form,
  Space,
  notification
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import { formatValue } from '../../utils/formatValue';
import {
  getMobilityOptions,
  getDeductibleOptions,
  getLegalOptions
} from '../../utils/milaOptions';

const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';

const useRCPFields = (form, supplier) => {
  const { t } = useTranslation();
  const {
    exemptions,
    formValues,
    setFormValues,
    codesNaf,
    estimatedCost,
    enums
  } = useQuotesContext();
  const [customGuaranteeLimit, setCustomGuaranteeLimit] = useState(false);

  const activities = form.getFieldValue('activities');
  const isAmendment = window.location.href.search('amendment');
  const [checkedVal, setCheckedVal] = useState('');
  // const [modalVisible, setModalVisible] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [customDeductibleAmount, setCustomDeductibleAmount] = useState(false);
  const [displayLegalProtection, setDisplayLegalProtection] = useState(false);
  const [displayMobilityGuarantee, setDisplayMobilityGuarantee] =
    useState(false);

  const suppliersArray = ['wk', 'ml'];

  const getLimitOptions = () => {
    if (supplier === 'ml') {
      return [50000, 100000, 150000, 200000, 300000, 500000, 750000, 1000000];
    }
    return suppliersArray.includes(supplier)
      ? [150000, 300000, 600000, 1000000, 1500000, 2000000]
      : [100000, 250000, 500000];
  };

  const [filteredOptions, setFilteredOptions] = useState(getLimitOptions());
  const [displayCustomOptions, setDisplayCustomOptions] = useState(true);
  const openNotificationWithIcon = (notificationType, value, descriptionKey) => {
    if (descriptionKey) {
      notification[notificationType]({
        message: t('contracts.form.warning_title'),
        description: t(descriptionKey),
        duration: 7,
        key: 'oneTime'
      });
    } else if (value) {
      notification[notificationType]({
        message: 'Notification de dérogation',
        description: t('help.activities'),
        duration: 7,
        key: 'oneTime'
      });
    } else {
      notification[notificationType]({
        message: t('contracts.form.contribution_adjustment_message'),
        key: 'oneTime'
      });
    }
  };

  const suppliersMap = {
    cm: 'CMAM',
    wk: 'WAKAM',
    ml: 'MILA'
  };

  const turnoverLevel = {
    cm: '1.000.000',
    ml: '750.000'
  };

  const maxTurnover = turnoverLevel[supplier] || '500.000';

  const supplierFullName = suppliersMap[supplier] || 'SMAB';

  const getCustom = () => {
    if (supplier === 'ml') {
      return 1000001;
    }
    return suppliersArray.includes(supplier) ? 2000001 : 500001;
  };

  const getCustomLimit = () => {
    return formValues?.guarantee_limit &&
      formValues?.guarantee_limit >= getCustom()
      ? formValues.guarantee_limit
      : getCustom();
  };

  const handleCompanyStatus = (value) => {
    if (value) {
      setDisplayCustomOptions(false);
      setFilteredOptions(
        getLimitOptions().filter((option) => option <= 150000)
      );
      setFormValues({
        ...formValues,
        self_entreprised: true,
        guarantee_limit: Math.min(formValues?.guarantee_limit || 0, 150000)
      });
      form.setFieldsValue({
        guarantee_limit: Math.min(formValues?.guarantee_limit || 0, 150000)
      });
    } else {
      setDisplayCustomOptions(true);
      setFilteredOptions(getLimitOptions());
      setFormValues({
        ...formValues,
        self_entreprised: false
      });
    }
  };

  const handleWantMobilityGuaranteeChange = (value) => {
    if (value) {
      setDisplayMobilityGuarantee(true);
      setFormValues({
        ...formValues,
        mobility_guarantee: 5000
      });
      form.setFieldsValue({
        mobility_guarantee: 5000
      });
    } else {
      setFormValues({
        ...formValues,
        mobility_guarantee: 0
      });
      form.setFieldsValue({
        mobility_guarantee: 0
      });
      setDisplayMobilityGuarantee(false);
    }
  };

  const getCustomLabel = () => {
    if (supplier === 'ml') return '+ de 1 000 000€';
    return suppliersArray.includes(supplier)
      ? '+ de 2 000 000€'
      : '+ de 500 000€';
  };

  const shouldShowCustomInput = () => {
    if (supplier === 'ml' && formValues?.guarantee_limit > 1000000) {
      return true;
    }
    if (
      suppliersArray.includes(supplier) &&
      supplier !== 'ml' &&
      formValues?.guarantee_limit >= 2000001
    ) {
      return true;
    }
    if (
      !suppliersArray.includes(supplier) &&
      formValues?.guarantee_limit > 500000
    ) {
      return true;
    }
    return false;
  };

  const handleLegalProtectionDisplayChange = (value) => {
    if (value) {
      setDisplayLegalProtection(true);
      form.setFieldsValue({
        legal_protection_choice: 1
      });
      setFormValues({
        ...formValues,
        legal_protection: true,
        extended_legal_protection: false
      });
    } else {
      form.setFieldsValue({
        legal_protection_choice: 0
      });
      setFormValues({
        ...formValues,
        legal_protection: false,
        extended_legal_protection: false
      });
      setDisplayLegalProtection(false);
    }
  };

  const getFrequencyOptions = () => {
    return ['MONTHLY', 'YEARLY'];
  };

  const handleLegalProtectionChange = (value) => {
    const transformedData = {
      legal_protection: value >= 1,
      extended_legal_protection: value === 2
    };

    setFormValues({
      ...formValues,
      ...transformedData
    });
  };

  const deductibleOptions =
    supplier === 'ml'
      ? getDeductibleOptions(formValues)
      : [1000, 750, 500, 250, 0];

  useEffect(() => {
    if (formValues && formValues.turnover >= 2000000 && supplier !== 'ml') {
      form.setFieldsValue({ extended_legal_protection: false });
      setFormValues({
        ...formValues,
        extended_legal_protection: false
      });
    }
    // eslint-disable-next-line
  }, [formValues?.turnover]);

  useEffect(() => {
    if (exemptions.includes(checkedVal)) {
      openNotificationWithIcon('warning', 'value');
    } // eslint-disable-next-line
  }, [exemptions.length]);

  useEffect(() => {
    if (activities) {
      let miniDeductibleAmount = 750;
      const selectedActivities = activities.map((a) => a.split(' - ')[0]);
      selectedActivities.forEach((activityName) => {
        const activity = codesNaf.find((act) => act.name === activityName);
        if (activity?.mini_deductible_amount > miniDeductibleAmount) {
          miniDeductibleAmount = activity?.mini_deductible_amount;
          form.setFieldsValue({ deductible_amount: miniDeductibleAmount });
          setFormValues({
            ...formValues,
            deductible_amount: miniDeductibleAmount
          });
        }
      });
    }
    // eslint-disable-next-line
  }, [activities?.length]);

  const rcpFields = [
    {
      name: ['activities'],
      tooltip: {
        name: 'activities_params',
        params: { max: supplier === 'ml' ? 5 : 10 }
      },
      rules: [{ required: true }],
      input: (
        <Select
          className="activities-select"
          mode="multiple"
          style={{ width: 400 }}
          options={codesNaf
            .filter((code) => code?.display?.RCP.includes(supplierFullName))
            .map(({ naf_code, name }) => ({
              value: `${name} - ${naf_code || 'NAF non renseigné'}`,
              label: `${name} - ${naf_code || 'NAF non renseigné'}`
            }))}
          filterOption={(input, option) =>
            formatValue(option.label).indexOf(formatValue(input)) > -1
          }
        />
      ),
      help:
        supplier === 'ml'
          ? exemptions.includes('activities')
            ? t('help.exemptions_activities_five')
            : exemptions.includes('derogatory_activity')
              ? t('help.derogatory_activity')
              : null
          : exemptions.includes('activities')
            ? t('help.exemptions_activities')
            : exemptions.includes('derogatory_activity')
              ? t('help.derogatory_activity')
              : null,
      step: 0
    },
    {
      name: ['turnover'],
      tooltip: {
        name: 'turnover_params',
        params: { max: maxTurnover }
      },
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: <InputNumber min={0} style={{ width: 120 }} />,
      help: exemptions.includes('turnover') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['employees_number'],
      tooltip: true,
      tooltipName: 'employees_number_step20',
      rules: [{ required: true }],
      input: <InputNumber min={0} />,
      help: exemptions.includes('employees_number')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['number_claims'],
      tooltip: true,
      rules: [{ required: true }],
      input: (
        <InputNumber
          min={0}
          onChange={(value) => {
            setFormValues({
              ...formValues,
              number_claims: value,
              ...(value > 0 && {
                deductible_amount: Math.max(
                  formValues?.deductible_amount || 1730,
                  830
                )
              })
            });
            if (value > 0)
              form.setFieldsValue({
                deductible_amount: Math.max(
                  formValues?.deductible_amount || 1730,
                  830
                )
              });
          }}
        />
      ),
      help: exemptions.includes('number_claims') ? t('help.exemptions') : null,
      step: 0
    },
    ...(formValues && formValues.number_claims >= 1
      ? [
          {
            name: ['claims'],
            noLabel: false,
            step: 0,
            help: exemptions.includes(`claim_amount`)
              ? t('help.exemptions')
              : null,
            input: (
              <Form.List name="claims">
                {(fields) => (
                  <div>
                    {fields.map((field) => (
                      <Space
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        align="baseline"
                      >
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_date']}
                          key={[field.fieldKey, 'claim_date']}
                          rules={[
                            {
                              required: true,
                              message: 'Date du sinistre manquant'
                            }
                          ]}
                        >
                          <DatePicker
                            format={dateFormat}
                            style={{ minWidth: 200 }}
                            disabledDate={(current) => {
                              return current >= moment();
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_type']}
                          key={[field.fieldKey, 'claim_type']}
                          rules={[
                            {
                              required: true,
                              message: 'Type du sinistre manquant'
                            }
                          ]}
                        >
                          <Input
                            style={{ width: '200px' }}
                            placeholder="Type de sinistre"
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_amount']}
                          key={[field.fieldKey, 'claim_amount']}
                          rules={[
                            {
                              required: true,
                              message: 'Montant du sinistre manquant'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '200px' }}
                            formatter={(value) => value && `${value}€`}
                            parser={(value) => value.replace('€', '')}
                            placeholder="Montant du sinistre"
                          />
                        </Form.Item>
                      </Space>
                    ))}
                  </div>
                )}
              </Form.List>
            )
          }
        ]
      : []),
    {
      name: ['company_been_insured'],
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.company_been_insured
      ? [
          {
            name: ['last_company_name'],
            input: <Input style={{ width: 500 }} />,
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['last_contract_number'],
            input: <Input style={{ width: 500 }} />,
            step: 0
          },
          {
            name: ['last_termination_date'],
            input: (
              <DatePicker
                format={dateFormat}
                style={{ minWidth: 200 }}
                onChange={(date) => {
                  setFormValues((prev) => {
                    const newValues = { ...prev, last_termination_date: date };
                    if (prev.termination_by_company) {
                      newValues.termination_date_by_company = date;
                    } else {
                      delete newValues.termination_date_by_company;
                    }
                    return newValues;
                  });
                }}
              />
            ),
            step: 0
          },
          {
            name: ['termination_by_company'],
            rules: [{ required: true }],
            input: (
              <PreRequisitesSwitch
                onChange={(checked) => {
                  setFormValues((prev) => {
                    const newValues = {
                      ...prev,
                      termination_by_company: checked
                    };
                    if (checked) {
                      newValues.termination_date_by_company =
                        prev.last_termination_date;
                    } else {
                      delete newValues.termination_date_by_company;
                    }
                    return newValues;
                  });
                }}
              />
            ),
            valuePropName: 'checked',
            tooltip: true,
            step: 0
          }
        ]
      : []),
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_reason_by_company'],
            rules: [{ required: true }],
            input: (
              <Select style={{ width: 220 }}>
                {enums?.termination_reason_by_company &&
                  enums.termination_reason_by_company.map((e) => (
                    <Option value={e} key={e}>
                      {t(`contracts.form.${e}`)}
                      <Tooltip
                        title={t(`contracts.form.tooltip.${e}`, {
                          rate: supplier === 'ml' ? 20 : 15
                        })}
                        overlayInnerStyle={{ width: 500 }}
                      >
                        <QuestionCircleOutlined style={{ float: 'right' }} />
                      </Tooltip>
                    </Option>
                  ))}
              </Select>
            ),
            help: exemptions.includes('termination_by_company')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.termination_reason_by_company === 'termination_other'
      ? [
          {
            name: ['termination_reason_by_company_custom'],
            rules: [{ required: true }],
            input: (
              <Input
                value={formValues.termination_reason_by_company_custom}
                onChange={(value) =>
                  setFormValues({
                    ...formValues,
                    termination_reason_by_company_custom: value
                  })
                }
                style={{ width: 250 }}
              />
            ),
            step: 0
          }
        ]
      : []),
    {
      name: ['guarantee_limit'],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.guarantee_limit', {
        threshold: supplier === 'ml' ? '300 000' : '600 000'
      }),
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          {filteredOptions.map((limit) => (
            <Radio.Button
              key={limit}
              onClick={() => setCustomGuaranteeLimit(false)}
              value={limit}
            >
              {`${limit.toLocaleString()}€`}
            </Radio.Button>
          ))}
          {displayCustomOptions && (
            <Radio.Button
              onClick={() => setCustomGuaranteeLimit(!customGuaranteeLimit)}
              value={getCustomLimit()}
            >
              {getCustomLabel()}
              {shouldShowCustomInput() ? (
                <InputNumber
                  value={formValues?.guarantee_limit}
                  onChange={(value) => {
                    setFormValues({
                      ...formValues,
                      guarantee_limit: value
                    });
                    form.setFieldsValue({ guarantee_limit: value });
                  }}
                  min={getCustom()}
                  style={{ width: 100, marginLeft: 8 }}
                />
              ) : null}
            </Radio.Button>
          )}
        </Radio.Group>
      ),
      help: exemptions.includes('guarantee_limit')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    ...(formValues &&
    (formValues?.guarantee_limit > 150000 || formValues?.self_entreprised) &&
    supplier === 'ml'
      ? [
          {
            name: ['self_entreprised'],
            tooltip: true,
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleCompanyStatus(value)}
              />
            ),
            step: 0
          }
        ]
      : []),
    {
      name: ['deductible_amount'],
      tooltip: true,
      tooltipName:
        supplier === 'ml' ? 'deductible_amount_ml' : 'deductible_amount',
      rules: [{ required: true }],
      input: (
        <Radio.Group
          buttonStyle="solid"
          onChange={(e) => {
            if (e.target.value === 0 && formValues?.key_man_guarantee) {
              form.setFieldsValue({ key_man_guarantee: false });
              setFormValues((prev) => ({ ...prev, key_man_guarantee: false }));
              openNotificationWithIcon(
                'warning',
                null,
                'contracts.form.key_man_guarantee_zero_deductible_warning'
              );
            }
          }}
        >
          {deductibleOptions.map((amount) => (
            <Radio.Button
              key={amount}
              onClick={() => setCustomDeductibleAmount(false)}
              value={amount}
            >
              {`${amount}€`}
            </Radio.Button>
          ))}

          {/* <Radio.Button
            onClick={() => {
              setCustomDeductibleAmount(true);
              setModalVisible(true);
            }}
            value={customDeductibleAmount && formValues?.deductible_amount}
          >
            {`Autre montant ${
              customDeductibleAmount && formValues?.deductible_amount
                ? `${formValues?.deductible_amount}€`
                : ''
            }`}
            {customDeductibleAmount && (
              <Modal
                open={modalVisible}
                centered
                width={400}
                title="Montant personnalisé"
                cancelText="Annuler"
                onOk={() => {
                  setModalVisible(false);
                }}
                onCancel={() => {
                  setModalVisible(false);
                  setCustomDeductibleAmount(false);
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <InputNumber
                    onChange={(change) => {
                      setFormValues((prevState) => ({
                        ...prevState,
                        deductible_amount: change
                      }));
                    }}
                    min={0}
                    defaultValue={0}
                    style={{ width: 80 }}
                  />
                </div>
              </Modal>
            )}
          </Radio.Button> */}
        </Radio.Group>
      ),
      help: exemptions.includes('deductible_amount')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    ...(supplier !== 'ml'
      ? [
          {
            name: ['legal_protection'],
            input: <Switch disabled />,
            valuePropName: 'checked',
            tooltip: true,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.legal_protection &&
    supplier !== 'ml' &&
    formValues &&
    formValues.turnover >= 0 &&
    formValues.turnover < 2000000
      ? [
          {
            name: ['extended_legal_protection'],
            tooltip: true,
            tooltipName: 'extended_legal_protection_turnover_limit',
            input: <PreRequisitesSwitch />,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    ...(supplier === 'ml'
      ? [
          {
            name: ['display_legal_protection'],
            tooltip: true,
            tooltipName: 'legal_protection_choice',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleLegalProtectionDisplayChange(value)}
              />
            ),
            step: 0
          },
          ...(displayLegalProtection
            ? [
                {
                  name: ['legal_protection_choice'],
                  input: (
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={(e) =>
                        handleLegalProtectionChange(e.target.value)
                      }
                    >
                      {getLegalOptions()
                        .filter((option) => option.value > 0)
                        .map((option) => (
                          <Radio.Button key={option.value} value={option.value}>
                            {t(`contracts.form.options.${option.label}`)}
                          </Radio.Button>
                        ))}
                    </Radio.Group>
                  ),
                  help: exemptions.includes('legal_protection')
                    ? t('help.exemptions')
                    : null,
                  step: 0
                }
              ]
            : [])
        ]
      : []),
    {
      name: ['exploitation_responsibility'],
      translate: false,
      label:
        formValues &&
        formValues.surface_area > 0 &&
        formValues.exploitation_responsibility
          ? `${t('contracts.form.exploitation_responsibility')} (${
              estimatedCost?.exploitation_responsibility_customer &&
              estimatedCost?.exploitation_responsibility_customer.toFixed(2)
            }€/mois)`
          : `${t('contracts.form.exploitation_responsibility')}`,
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      tooltip: true,
      step: 0
    },
    ...(formValues &&
    formValues.exploitation_responsibility &&
    supplier !== 'ml'
      ? [
          {
            name: ['surface_area'],
            rules: [{ required: true }],
            tooltip: true,
            input: <InputNumber min={0} />,
            help: exemptions.includes('surface_area')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(supplier !== 'wk'
      ? [
          {
            name: ['social_mandate_responsibility'],
            tooltip: true,
            tooltipName: 'rcms_ml',
            input: <PreRequisitesSwitch />,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    ...(supplier === 'ml'
      ? [
          {
            name: ['key_man_guarantee'],
            tooltip: true,
            tooltipName: 'key_man_guarantee',
            help:
              formValues?.deductible_amount === 0
                ? t('contracts.form.key_man_guarantee_unavailable')
                : null,
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                disabled={formValues?.deductible_amount === 0}
              />
            ),
            step: 0
          }
        ]
      : []),
    ...(supplier === 'ml'
      ? [
          {
            name: ['display_mobility_guarantee'],
            tooltip: true,
            tooltipName: 'mobility_guarantee',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleWantMobilityGuaranteeChange(value)}
              />
            ),
            step: 0
          }
        ]
      : []),
    ...(supplier === 'ml' && displayMobilityGuarantee
      ? [
          {
            name: ['mobility_guarantee'],
            label: 'mobility_guarantee_rates',
            input: (
              <Radio.Group buttonStyle="solid">
                {getMobilityOptions()
                  .filter((option) => option.value > 0)
                  .map((option) => (
                    <Radio.Button key={option} value={option.value}>
                      {t(`contracts.form.options.${option.label}`)}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            ),
            help: exemptions.includes('mobility_guarantee')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(supplier !== 'wk'
      ? []
      : [
          {
            name: ['is_in_liquidation_or_receivership'],
            input: <PreRequisitesSwitch />,
            valuePropName: 'checked',
            help: exemptions.includes('is_in_liquidation_or_receivership')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          {getFrequencyOptions().map((frequency) => (
            <Radio.Button key={frequency} value={frequency}>
              {t(`contracts.form.options.${frequency}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['religious_pol'],
      step: 1
    },
    ...(supplier === 'wk'
      ? [
          {
            name: ['industrial_activity'],
            step: 1
          },
          {
            name: ['combustible_packaging'],
            step: 1
          },
          {
            name: ['wholesale_business'],
            step: 1
          },
          {
            name: ['straw_rubber_activity'],
            step: 1
          },
          {
            name: ['metropolitan_france'],
            step: 1
          }
        ]
      : []),
    ...(supplier === 'ml'
      ? [
          {
            name: ['industrial_straw_activity'],
            step: 1
          },
          {
            name: ['construction_activity'],
            step: 1
          },
          {
            name: ['medical_activity'],
            step: 1
          },
          {
            name: ['legal_activity'],
            step: 1
          },
          {
            name: ['has_already_been_terminated_by_asx'],
            step: 1
          },
          {
            name: ['france_with_corsica'],
            step: 1
          }
        ]
      : [])
  ];

  return {
    rcpFields: rcpFields.map((f) => {
      if (f.step === 1)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          labelAlign: 'left',
          valuePropName: 'checked',
          withDivider: true,
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default useRCPFields;
